import React, { useEffect, useMemo } from 'react';

import { ResourceIdDto, ResourceTimelineMessageListItemDto } from '@/api/generated';
import { CustomTableFilterDefinition } from '@/components/table/components/formBuilder/model/CustomTableFilterDefinition.model.ts';
import { CustomTableFilterTypeEnum } from '@/components/table/components/formBuilder/model/CustomTableFilterTypeEnum.ts';
import { CustomTable, CustomTableColumn } from '@/components/table/CustomTable.comp.tsx';
import { CustomTableEventModel } from '@/components/table/model/CutomTableEvent.model.ts';
import { customTableEventDispatcher } from '@/components/table/util/customTableEventDispatcher.util.ts';
import { TimelineMessagesTableParams } from '@/components/timelineMessages/comp/confirmMessagesReadButton/model/TimelineMessagesTableParams.model.ts';
import { ResourceTimelineNewEmailButton } from '@/components/timelineMessages/comp/newEmailButton/ResourceTimelineNewEmailButton.comp.tsx';
import { ResourceTimelineMessagesTableRow } from '@/components/timelineMessages/comp/tableRow/ResourceDetailsTasksTableRow.comp.tsx';
import {
  initialResourceTimelineMessagesTableState,
  ResourceTimelineMessagesTableState,
} from '@/components/timelineMessages/model/ResourceTimelineMessagesTableState.model.ts';
import { useResourcesTimelineMessagesFormValidation } from '@/components/timelineMessages/util/useResourcesTimelineMessagesForm.validation.ts';
import { GlobalEvent } from '@/utils/globalEventDispatcher.util.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix';
import { useGetResourceTimelineMessages } from '@/views/processesDetails/util/useGetResourceTimelineMessages.util.ts';

import { ResourceTimelineMessagesFormModel } from '../model/ResourceTimelineMessagesForm.model';

type Props = {
  resource: ResourceIdDto;
};

export const ResourceTimelineMessagesTable: React.FC<Props> = ({ resource }) => {
  const { t } = useTranslationWithPrefix('components.timelineMessages');
  const { getTimelineMessages, isLoading } = useGetResourceTimelineMessages({
    resource,
  });
  const validation = useResourcesTimelineMessagesFormValidation();

  const filters: CustomTableFilterDefinition<ResourceTimelineMessagesFormModel>[] = useMemo(
    () => [
      {
        label: t('head.createdFrom'),
        name: 'createdFrom',
        type: CustomTableFilterTypeEnum.Date,
        xs: 3,
      },
      {
        label: t('head.createdTo'),
        name: 'createdTo',
        type: CustomTableFilterTypeEnum.Date,
        xs: 3,
      },
    ],
    [t],
  );

  useEffect(() => {
    const refreshTable = () => customTableEventDispatcher(CustomTableEventModel.REFRESH);
    window.addEventListener(GlobalEvent.EMAIL_DRAFT_UPDATED, refreshTable);
    return () => {
      window.removeEventListener(GlobalEvent.EMAIL_DRAFT_UPDATED, refreshTable);
    };
  }, []);

  const tableHead: CustomTableColumn<string>[] = useMemo(() => [], []);

  const init = async (initialState: ResourceTimelineMessagesTableState) => {
    return await getTimelineMessages(initialState);
  };

  return (
    <CustomTable<
      string,
      ResourceTimelineMessagesFormModel,
      ResourceTimelineMessageListItemDto,
      TimelineMessagesTableParams
    >
      additionalButtons={[
        {
          button: ResourceTimelineNewEmailButton,
          name: 'newEmail',
          params: { resource },
        },
      ]}
      CustomTableRow={ResourceTimelineMessagesTableRow}
      filters={filters}
      getTableDataRequest={getTimelineMessages}
      idKey="resourceId"
      init={init}
      initParams={initialResourceTimelineMessagesTableState}
      isLoading={isLoading}
      size="small"
      tableHead={tableHead}
      tableKey={`messages_${resource.resourceId}`}
      tableSx={{
        borderCollapse: 'collapse',
        overflowX: 'clip',
        tableLayout: 'fixed',
        width: '100%',
      }}
      validation={validation}
    />
  );
};
